<template>
  <div
    class="nx-url-selector input-group v-text-field text--primary mb-4"
  >
    <label class="v-label--active v-label theme--light">
      {{ label | capitalize }}
    </label>

    <div class="nx-primary--lighten-5 pa-3">
      <nx-form :scope="scope">
        <nx-path-form-selector
          :label="$t('message.form.fields.urlSelector.path')"
          :value="draft.path"
          :suggest-context="suggestContext"
          name="path"
          :disabled="disabled || currentUrlType.redirect"
          @input="updateField('path', $event)"
        />

        <select-field
          v-if="validTypesOptions.length > 0"
          name="urlType"
          :disabled="disabled || validTypesOptions.length === 1"
          :items="validTypesOptions"
          :value="draft.type"
          :label="$t('message.form.fields.urlType')"
          @input="updateUrlType"
        />

        <!-- Formulario para BoardUrl: opción para generar automáticamente, o selector de portada-->
        <div v-show="currentUrlType.board">
          <transition name="slide-y-transition">
            <nx-switch
              v-show="visible.autogenerateBoard"
              :value="draft.autogenerateBoard"
              :disabled="disabled"
              :label="$t('message.form.fields.urlSelector.autogenerateBoard')"
              name="autogenerateBoard"
              hide-details
              @input="updateAutogenerateBoard"
            />
          </transition>

          <transition name="slide-y-transition">
            <nx-board-form-selector
              v-show="visible.board"
              key="board-selector"
              :label="$t('message.form.fields.urlSelector.entity')"
              :value="draft.entity"
              :disabled="disabled"
              name="entity"
              :validation="{ required: required.board }"
              :additional-filters="computedBoardSelectorAdditionalFilters"
              :wizards="boardWizards"
              :disabled-filters="boardSelectorDisabledFilters"
              :disable-board-creation="disabled"
              @input="updateEntityIdValue"
            />
          </transition>
        </div>

        <!-- Formulario para RedirectUrl: código de redirección y entidad de destino. -->
        <div v-show="currentUrlType.redirect">
          <template v-for="(field, index) in redirectConfig.form">
            <nx-form-generator-scope
              :key="index"
              ref="form"
              :config="field"
              :show-tabs="false"
              :value="value"
              @input="onRedirectInput"
              @form="isValid = $event.valid"
            />
          </template>
        </div>
      </nx-form>
    </div>
  </div>
</template>

<script>
import uuid from 'uuid/v4';
import cloneDeep from 'lodash/cloneDeep';
import isObject from 'lodash/isObject';
import NxForm from '@/modules/form/Form';
import NxBoardFormSelector from '@/modules/board/selector/FormSelector';
import NxFormGeneratorScope from '@/modules/form/FormScopeGenerator';
import { createRedirectFormConfig } from '@/modules/redirect/config';
import NxPathFormSelector from '@/modules/url/components/PathFormSelector';

export default {
  name: 'NxUrlSelector',
  components: {
    NxPathFormSelector,
    NxBoardFormSelector,
    NxFormGeneratorScope,
    NxForm,
  },
  extends: {},
  mixins: [],
  props: {
    allowedTypes: {
      type: Array,
      default: null,
      validator: value => value.length > 0,
    },
    boardWizards: {
      type: Array,
      default: () => ([]),
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    label: {
      type: String,
      default: '',
    },
    // @see https://mgr.bitban.com/issues/67493
    enableAutogenerateBoardCheck: {
      type: Boolean,
      default: false,
    },
    enableTypeRedirect: Boolean,
    enableTypeContent: Boolean,
    disabled: Boolean,
    boardSelectorAdditionalFilters: {
      type: Object,
      default: () => ({}),
    },
    suggestContext: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      draft: {},
      scope: uuid(),
      redirectConfig: createRedirectFormConfig(),
    };
  },
  computed: {
    currentUrlType() {
      return Object.entries(this.urlTypes)
        .reduce((acc, [type, value]) => {
          acc[type] = this.draft.type === value;
          return acc;
        }, {});
    },
    required() {
      return {
        board: this.currentUrlType.board
          && this.draft.path
          && !this.draft.autogenerateBoard,
      };
    },
    visible() {
      return {
        // El check para autogenerar la portada manual de una taxonomía se mostrará cuando:
        autogenerateBoard: this.enableAutogenerateBoardCheck // esté habilitado,
          && this.validTypes.board // se pueda seleccionar el tipo BoardUrl y TaxonUrl,
          && this.validTypes.taxon
          && this.currentUrlType.board // el tipo seleccionado actualmente sea BoardUrl
          && !this.draft.entity, // y no haya portada seleccionada.
        board: this.currentUrlType.board
          && !this.draft.autogenerateBoard,
      };
    },
    urlTypes() {
      return {
        board: 'boardUrl',
        content: 'contentUrl',
        taxon: 'taxonUrl',
        redirect: 'redirectUrl',
      };
    },
    validTypes() {
      const allowedTypesDefined = Array.isArray(this.allowedTypes);
      const allowedTypes = allowedTypesDefined
        ? this.allowedTypes
        : Object.values(this.urlTypes);
      return {
        board: allowedTypes.includes(this.urlTypes.board),
        content: allowedTypes.includes(this.urlTypes.content) && (allowedTypesDefined || this.enableTypeContent),
        taxon: allowedTypes.includes(this.urlTypes.taxon),
        redirect: allowedTypes.includes(this.urlTypes.redirect) && (allowedTypesDefined || this.enableTypeRedirect),
      };
    },
    validTypesOptions() {
      return Object.entries(this.validTypes)
        .filter(([type, valid]) => valid)
        .map(([type, valid]) => ({
          text: this.$t(`message.form.fields.urlSelector.urlType.${type}`),
          value: this.urlTypes[type],
        }));
    },
    computedBoardSelectorAdditionalFilters() {
      const filters = {
        behaviour: 'unlinked',
      };
      return {
        ...filters,
        ...this.boardSelectorAdditionalFilters,
      };
    },
    boardSelectorDisabledFilters() {
      // Disable editing behaviour and state filters, don't have sense.
      return Object.keys(this.computedBoardSelectorAdditionalFilters);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.draft = cloneDeep(isObject(this.value) ? this.value : {});
        this.draft.type = this.draft.type || this.getDefaultType();
        this.draft.siteId = this.draft.siteId || this.$store.getters.siteId;
      },
    },
  },
  methods: {
    /**
     * TODO: Cuando se añada soporte para otro tipo de URLs además de 'taxonUrl' y 'boardUrl', esta
     * función puede ser simplificada. El formulario podría mostrar un select o un radio-group, y
     * así actualizar el valor con @input.
     * @param {'boardUrl'|'contentUrl'|'taxonUrl'|'redirectUrl'} urlType
     */
    updateUrlType(urlType) {
      this.updateField('entity', null, false);
      this.updateField('type', urlType, false);
      if (this.enableAutogenerateBoardCheck && urlType === this.urlTypes.board) {
        this.updateField('autogenerateBoard', true, false);
      }
      this.emitDraft();
    },
    updateAutogenerateBoard(autogenerate) {
      this.updateField('autogenerateBoard', autogenerate, true);
    },
    onRedirectInput(field, value) {
      this.updateField(field, value, true);
    },
    emitDraft() {
      this.$emit('input', this.draft);
    },
    updateEntityIdValue(id, entity) {
      this.updateField('entity', entity);
    },
    updateField(field, value, emit = true) {
      this.$set(this.draft, field, value);
      console.log('updated draft', field, value, this.draft);
      if (emit) this.emitDraft();
    },
    getDefaultType() {
      if (this.validTypes.taxon) return this.urlTypes.taxon;
      if (this.validTypes.board) return this.urlTypes.board;
      return this.urlTypes.content;
    },
  },
};
</script>

<style lang="stylus">
.nx-url-selector {

  position: relative

  > label.v-label {
    top: 15px
    position: absolute
  }

}
</style>
